import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, MapPin, Home, Building2, Mail, Phone, Download, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";

const ACCENT = "#00E676";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "investor", label: "Investor", desc: "Yield-driven childcare & commercial assets", icon: TrendingUp },
  { id: "landowner", label: "Landowner", desc: "Sell or JV your NSW site", icon: MapPin },
  { id: "homebuyer", label: "Homebuyer", desc: "Villas, estates & custom homes", icon: Home },
  { id: "tenant", label: "Commercial Tenant", desc: "Retail, medical & office suites", icon: Building2 },
];

const INTERESTS = [
  "Childcare Centre — Build & Lease",
  "Gated Masterplanned Community",
  "Bespoke Luxury Villa",
  "Commercial Complex / Retail Hub",
  "Requirement-Based Custom Build",
  "General Enquiry",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/25";

const fmtM = (v) => (v >= 1 ? `$${v.toFixed(1)}M` : `$${Math.round(v * 1000)}k`);

export const QueryWizard = () => {
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState("");
  const [interest, setInterest] = useState(INTERESTS[0]);
  const [budget, setBudget] = useState(2);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [refId, setRefId] = useState("");

  const next = () => {
    if (step === 0 && !category) {
      toast.error("Pick the option that sounds like you.");
      return;
    }
    setStep((s) => Math.min(s + 1, 3));
  };

  const submit = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("We need at least your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads/query`, {
        category,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        budget: Math.round(budget * 1_000_000),
        interest,
        message: message.trim(),
      });
      setRefId(data.id);
      setStep(3);
      toast.success("Enquiry received — we'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadBrochure = async () => {
    try {
      const res = await axios.get(`${API}/brochure`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "JAI-HOMES-Brochure.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't fetch the brochure — try again.");
    }
  };

  return (
    <section id="contact" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36 border-t border-white/10" data-testid="query-hub-section">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-12"
        >
          <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#00E676] mb-4">
            CHAPTER 05 — THE QUERY HUB
          </p>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02]">
            Start the conversation.{" "}
            <em className="font-serif-lux italic font-semibold" style={{ color: ACCENT }}>
              We'll bring the blueprint.
            </em>
          </h2>
          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="direct-contact-block">
            <a
              href="mailto:admin@jaihomes.com.au"
              data-testid="direct-email-link"
              className="inline-flex items-center gap-2 font-display text-sm font-semibold text-white/70 hover:text-[#00E676] transition-colors duration-300"
            >
              <Mail size={16} /> admin@jaihomes.com.au
            </a>
            <a
              href="tel:0272395450"
              data-testid="direct-phone-link"
              className="inline-flex items-center gap-2 font-display text-sm font-semibold text-white/70 hover:text-[#00E676] transition-colors duration-300"
            >
              <Phone size={16} /> (02) 7239 5450 · 0413 328 202
            </a>
          </div>
          <p className="mt-3 font-display text-[11px] font-bold tracking-[0.2em] text-white/40" data-testid="query-hub-licence">
            NSW BUILDER LICENCE 476816C — "WHERE DREAMS BECOME REALITY"
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          className="glass rounded-2xl border border-white/10 p-8 sm:p-10"
          style={{ boxShadow: "0 0 60px rgba(0,230,118,0.06)" }}
          data-testid="query-wizard"
        >
          {step < 3 && (
            <div className="flex items-center gap-2 mb-10" data-testid="wizard-progress">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="h-1.5 rounded-full transition-all duration-500"
                  style={{
                    width: step === i ? 44 : 20,
                    background: i <= step ? ACCENT : "rgba(255,255,255,0.12)",
                    boxShadow: i === step ? `0 0 12px ${ACCENT}` : "none",
                  }}
                />
              ))}
              <span className="ml-auto font-display text-[11px] tracking-[0.2em] text-white/40">
                STEP {step + 1} OF 3
              </span>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -32 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && (
                <div>
                  <h3 className="font-display text-xl font-bold text-white mb-6">Which one sounds like you?</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {CATEGORIES.map((c) => {
                      const Icon = c.icon;
                      const active = category === c.id;
                      return (
                        <button
                          key={c.id}
                          data-testid={`wizard-category-${c.id}`}
                          onClick={() => setCategory(c.id)}
                          className={`rounded-xl border p-5 text-left transition-all duration-300 ${
                            active
                              ? "border-[#00E676] bg-[#00E676]/10 shadow-[0_0_24px_rgba(0,230,118,0.2)]"
                              : "border-white/10 bg-white/[0.03] hover:border-white/30"
                          }`}
                        >
                          <Icon size={22} style={{ color: active ? ACCENT : "rgba(255,255,255,0.5)" }} />
                          <p className="mt-3 font-display text-sm font-bold text-white">{c.label}</p>
                          <p className="mt-1 text-xs text-white/50">{c.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div>
                  <h3 className="font-display text-xl font-bold text-white mb-6">What are you chasing?</h3>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {INTERESTS.map((int) => (
                      <button
                        key={int}
                        data-testid={`wizard-interest-${int.split(" ")[0].toLowerCase()}`}
                        onClick={() => setInterest(int)}
                        className={`rounded-full px-4 py-2 font-display text-xs font-semibold border transition-all duration-300 ${
                          interest === int
                            ? "border-[#00E676] bg-[#00E676]/15 text-white"
                            : "border-white/10 text-white/50 hover:border-white/30"
                        }`}
                      >
                        {int}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between items-baseline mb-3">
                    <label className="font-display text-xs font-bold tracking-[0.2em] text-white/50">BUDGET RANGE</label>
                    <span className="font-display text-lg font-bold text-white" data-testid="wizard-budget-value">{fmtM(budget)}+</span>
                  </div>
                  <input
                    type="range"
                    min={0.25}
                    max={20}
                    step={0.25}
                    value={budget}
                    onChange={(e) => setBudget(parseFloat(e.target.value))}
                    className="lux-range"
                    style={{ "--range-accent": ACCENT }}
                    data-testid="wizard-budget-slider"
                  />
                  <textarea
                    className={`${inputCls} mt-7 min-h-[88px] resize-none`}
                    placeholder="Anything else? Timeline, location, questions..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    data-testid="wizard-message"
                  />
                </div>
              )}

              {step === 2 && (
                <div>
                  <h3 className="font-display text-xl font-bold text-white mb-6">Where do we send the feasibility?</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input className={inputCls} placeholder="Full name *" value={name} onChange={(e) => setName(e.target.value)} data-testid="wizard-name" />
                    <input className={inputCls} type="email" placeholder="Email *" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="wizard-email" />
                    <input className={`${inputCls} sm:col-span-2`} placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="wizard-phone" />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="text-center py-6" data-testid="wizard-success">
                  <CheckCircle2 size={52} style={{ color: ACCENT }} className="mx-auto mb-5" />
                  <h3 className="font-display text-2xl font-extrabold text-white tracking-tight">
                    Beauty — you're in the system.
                  </h3>
                  <p className="mt-3 text-sm text-white/60 max-w-md mx-auto leading-relaxed">
                    Reference <span className="text-white font-semibold">{refId.slice(0, 8).toUpperCase()}</span>.
                    Our team will reach out within 2 business days. Meanwhile, grab the full
                    JAI HOMES capability brochure.
                  </p>
                  <button
                    onClick={downloadBrochure}
                    data-testid="brochure-download-btn"
                    className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-display text-sm font-bold text-[#0A0E17] bg-[#00E676] hover:shadow-[0_0_28px_rgba(0,230,118,0.5)] hover:scale-[1.03] transition-all duration-300"
                  >
                    <Download size={16} /> Download the Brochure (PDF)
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {step < 3 && (
            <div className="mt-10 flex items-center justify-between">
              <button
                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                disabled={step === 0}
                data-testid="wizard-back-btn"
                className="inline-flex items-center gap-2 font-display text-sm font-semibold text-white/50 hover:text-white disabled:opacity-0 transition-colors duration-300"
              >
                <ArrowLeft size={16} /> Back
              </button>
              {step < 2 ? (
                <button
                  onClick={next}
                  data-testid="wizard-next-btn"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-display text-sm font-bold text-[#0A0E17] bg-[#00E676] hover:shadow-[0_0_24px_rgba(0,230,118,0.45)] hover:scale-[1.03] transition-all duration-300"
                >
                  Continue <ArrowRight size={16} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={submitting}
                  data-testid="wizard-submit-btn"
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 font-display text-sm font-bold text-[#0A0E17] bg-[#00E676] hover:shadow-[0_0_24px_rgba(0,230,118,0.45)] hover:scale-[1.03] transition-all duration-300 disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "Send My Enquiry"} <ArrowRight size={16} />
                </button>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
